<template>
  <div @mousewheel="handleMouseWheel">
    <div class="timeline-header">
      <timeline-handle />
      <div class="timeline-operation-area">
        <el-tooltip content="剪辑" placement="top" :disabled="!canSplit">
          <a-button
            class="timeline-operation-area__btn ant-btn-icon-only"
            size="small"
            type="link"
            :disabled="!canSplit"
            @click="split"
          >
            <a-icon :component="icons.scissor" />
          </a-button>
        </el-tooltip>
        <el-tooltip content="定格" placement="top" :disabled="!canFix">
          <a-button
            class="timeline-operation-area__btn ant-btn-icon-only"
            size="small"
            type="link"
            :disabled="!canFix"
            @click="fixFrame"
          >
            <a-icon :component="icons.fix" />
          </a-button>
        </el-tooltip>
        <el-tooltip content="删除" placement="top" :disabled="!canDelete">
          <a-button
            class="timeline-operation-area__btn ant-btn-icon-only"
            size="small"
            type="link"
            @click="autoRemoveSelection"
            :disabled="!canDelete"
          >
            <a-icon :component="icons.delete" />
          </a-button>
        </el-tooltip>
        <timeline-filter v-model="activeFilter" />
      </div>
      <record class="timeline-operation-record"></record>
      <timeline-zoom :scale="scale" @change="handleScaleChange" />
      <duration-editor :current.sync="current" v-model="duration" />
      <div class="timeline-controls">
        <el-tooltip content="重新播放" placement="top">
          <a-button
            shape="circle"
            class="timeline-controls__button"
            icon="reload"
            size="small"
            @click="reload"
          />
        </el-tooltip>
        <el-tooltip :content="playing ? '暂停' : '片段预览'" placement="top">
          <a-button
            @click="togglePlay"
            shape="circle"
            class="timeline-controls__button"
            :icon="playing ? 'pause' : 'caret-right'"
            size="small"
          />
        </el-tooltip>
        <el-tooltip content="停止播放" placement="top">
          <a-button
            shape="circle"
            class="timeline-controls__button"
            size="small"
            @click="stop"
          >
            <i class="icon-stop-button"></i>
          </a-button>
        </el-tooltip>
      </div>
    </div>
    <div class="slide-container">
      <selection-actions />
      <div
        class="slide-container__body"
        :style="{ overflow: offset ? 'hidden' : 'visible' }"
      >
        <div
          class="slide-container__box slide-container__box--shadowed"
          :style="{
            width: duration * 0.1 * scale + 'px',
            transform: 'translate(' + -offset + 'px)'
          }"
        >
          <timeline-scale
            :duration="duration"
            :availableWidth="availableWidth"
            :offset="offset"
            :scale="scale"
          />
          <el-slider
            class="slide-container__slider"
            :max="duration"
            :formatTooltip="formatTooltip"
            v-model="current"
            :disabled="playing"
          />
        </div>
        <div
          class="slide-container__snap"
          :class="{ 'slide-container__snap--current': snapping }"
          :style="{ left: snapTime * 0.1 * scale + 'px' }"
        />
      </div>
    </div>
    <div class="timeline-tracks" :style="{ height: height - 86 + 'px' }">
      <track-group
        :label="`音频 (${audios.length})`"
        type="audioExpand"
        :total="duration"
        :scale="scale"
        :offset="offset"
        v-show="audios.length"
        :audioExpanded.sync="audioShowing"
      />
      <track-group
        :label="'音频: ' + audio.name"
        type="audio"
        :scale="scale"
        :offset="offset"
        :total="duration"
        v-for="audio in audios"
        :key="'audio' + audio.id"
        :tracks="[audio]"
        @changetracks="handleAudioChange"
        @click="clickCurrentAudio"
        v-show="audioShowing"
        :snap="snappable"
        :current="current"
        @snap="handleTrackSnap"
      />
      <track-group
        type="effect"
        :total="duration"
        v-for="anim in filteredAnimations"
        v-model="anim.tracks"
        :effect="anim"
        :key="'effect' + anim.id"
        :scale="scale"
        :offset="offset"
        :snap="snappable"
        :current="current"
        @snap="handleTrackSnap"
      />
      <track-group
        label="背景"
        type="background"
        :total="duration"
        :scale="scale"
        :offset="offset"
      />
    </div>
    <timeline-scroll
      :timelineWidth="scrollAreaWidth"
      :availableWidth.sync="availableWidth"
      :padding="250"
      v-model="offset"
      ref="scroll"
    />
  </div>
</template>

<script>
import TrackGroup from './TrackGroup';
import TimelineScroll from './TimelineScroll';
import TimelineHandle from './TimelineHandle';
import TimelineZoom from './TimelineZoom';
import SelectionActions from './SelectionActions';
import DurationEditor from './DurationEditor';
import TimelineFilter from './TimelineFilter';
import TimelineScale from './TimelineScale';
import Record from './Record/Record'

import { clamp, formatTimeInterval, isMac } from '../../utils';
import { matchFilter } from '../../utils/util';
import {mapState} from "vuex";
const icons = {
  scissor: {
    render() {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 1024 1024' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M908.437333 205.824l-1.770666 2.026667-307.2 307.2 307.2 307.178666a21.333333 21.333333 0 0 1 0 30.165334l-30.165334 30.165333a21.333333 21.333333 0 0 1-30.165333 0l-307.2-307.2-110.357333 110.357333a170.666667 170.666667 0 1 1-60.330667-60.330666l110.357333-110.357334-108.586666-108.629333a170.666667 170.666667 0 1 1 59.605333-61.034667l109.333333 109.312L846.336 147.498667a21.333333 21.333333 0 0 1 28.16-1.770667l2.005333 1.770667L906.666667 177.664a21.333333 21.333333 0 0 1 1.770666 28.16z m-541.354666 566.613333a85.333333 85.333333 0 1 0-170.666667 0 85.333333 85.333333 0 0 0 170.666667 0z m0-512a85.333333 85.333333 0 1 0-170.666667 0 85.333333 85.333333 0 0 0 170.666667 0z' }
          })
        ]);
    }
  },
  delete: {
    render() {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 1024 1024' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M981.138286 192.877714H730.770286V179.931429C727.917714 79.872 636.416 0 522.825143 0 409.819429 0 318.244571 79.872 315.977143 179.931429v12.946285H43.008c-24.868571 0-42.934857 16.310857-42.934857 37.083429 0 20.845714 18.066286 37.156571 42.934857 37.156571H149.284571v577.536c0 142.262857 20.918857 179.346286 143.506286 179.346286h438.564572c123.172571 0 143.506286-35.986286 143.506285-179.346286V267.117714h106.276572c24.868571 0 42.934857-16.310857 42.934857-37.156571 0-20.187429-19.748571-37.083429-42.934857-37.083429zM520.557714 67.510857c68.388571 0 125.44 47.177143 128.292572 109.056l0.512 14.043429h-258.194286l1.097143-14.628572c5.632-60.708571 61.586286-108.470857 128.292571-108.470857zM297.910857 955.977143c-61.074286 0-74.605714-29.257143-74.605714-111.908572V267.117714h577.536v588.8c0 39.862857-1.097143 53.906286-8.484572 69.12-10.752 21.357714-31.597714 30.939429-65.536 30.939429H297.910857z m113.005714-548.864c-28.233143 0-48.566857 25.307429-48.566857 54.564571v310.418286c0 29.769143 20.333714 54.491429 48.566857 54.491429s48.566857-25.234286 48.566858-54.491429V461.677714c0-29.842286-23.113143-54.564571-48.566858-54.564571z m202.313143 0c-28.233143 0-48.566857 25.307429-48.566857 54.564571v310.418286c0 29.769143 23.113143 54.491429 48.566857 54.491429 28.233143 0 48.566857-25.234286 48.566857-54.491429V461.677714a49.371429 49.371429 0 0 0-48.566857-54.564571z' }
          })
        ]);
    }
  },
  fix: {
    render() {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 14 14' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M14,14 L12.775,14 L12.775,13.4545455 L13.475,13.4545455 L13.475,12.7272727 L14,12.7272727 L14,14 Z M11.8125,14 L10.9375,14 L10.9375,13.4545455 L11.8125,13.4545455 L11.8125,14 Z M9.1,14 L7,14 L7,13.4545455 L9.1,13.4545455 L9.1,14 Z M7,14 L4.9,14 L4.9,13.4545455 L7,13.4545455 L7,14 Z M3.0625,14 L2.1875,14 L2.1875,13.4545455 L3.0625,13.4545455 L3.0625,14 Z M1.225,14 L0,14 L0,12.7272727 L0.525,12.7272727 L0.525,13.4545455 L1.225,13.4545455 L1.225,14 Z M0.525,11.1818182 L0,11.1818182 L0,9.45454545 L0.525,9.45454545 L0.525,11.1818182 L0.525,11.1818182 Z M0.525,7.90909091 L0,7.90909091 L0,6.09090909 L0.525,6.09090909 L0.525,7.90909091 Z M0.525,4.54545455 L0,4.54545455 L0,2.81818182 L0.525,2.81818182 L0.525,4.54545455 Z M0.525,1.27272727 L0,1.27272727 L0,0 L1.225,0 L1.225,0.545454545 L0.525,0.545454545 L0.525,1.27272727 Z M11.8125,0.545454545 L10.9375,0.545454545 L10.9375,0 L11.8125,0 L11.8125,0.545454545 L11.8125,0.545454545 Z M9.1,0.545454545 L7,0.545454545 L7,0 L9.1,0 L9.1,0.545454545 L9.1,0.545454545 Z M7,0.545454545 L4.9,0.545454545 L4.9,0 L7,0 L7,0.545454545 L7,0.545454545 Z M3.0625,0.545454545 L2.1875,0.545454545 L2.1875,0 L3.0625,0 L3.0625,0.545454545 L3.0625,0.545454545 Z M14,1.27272727 L13.475,1.27272727 L13.475,0.545454545 L12.775,0.545454545 L12.775,0 L14,0 L14,1.27272727 Z M14,11.1818182 L13.475,11.1818182 L13.475,9.45454545 L14,9.45454545 L14,11.1818182 Z M14,7.90909091 L13.475,7.90909091 L13.475,6.09090909 L14,6.09090909 L14,7.90909091 Z M14,4.54545455 L13.475,4.54545455 L13.475,2.81818182 L14,2.81818182 L14,4.54545455 Z M2.8,0 L11.2,0 L11.2,14 L2.8,14 L2.8,0 Z' }
          })
        ]);
    }
  }
}

export default {
  data() {
    return {
      scale: 1,
      offset: 0,
      availableWidth: 0,
      icons,
      audioShowing: true,
      snapping: false,
      snapTime: 0
    };
  },
  computed: {
    ...mapState('record', ['recordStatus']),
    activeFragment() {
      return this.$store.getters.activeFragment;
    },
    selectedEffectId() {
      return this.$store.getters.selectedEffectId;
    },
    selectedEffect() {
      return this.$store.getters.selectedEffect;
    },
    audios: {
      get() {
        return this.activeFragment.audios.map(id => this.$store.state.audios[id]);
      },
      set(value) {
        const audioCopies = {
          ...this.$store.state.audios
        };
        const fragAudioIds = value.map(v => {
          audioCopies[v.id] = v;
          return v.id;
        });
        this.$store.commit('handleAudios', audioCopies);
        this.$store.dispatch('changeActiveFragment', {
          ...this.activeFragment,
          audios: fragAudioIds
        });
      }
    },
    duration: {
      get() {
        return this.activeFragment.duration;
      },
      set(value) {
        this.$store.dispatch('handleFragmentsDuration', value);
      }
    },
    current: {
      get() {
        return this.$store.state.currentProgress - this.activeFragment.start;
      },
      set(value) {
        this.$store.commit('handleCurrentProgress', value + this.activeFragment.start);
      }
    },
    playing: {
      get() {
        return this.$store.state.playing;
      },
      set(value) {
        this.$store.commit('handlePlaying', value);
      }
    },
    animations() {
      return this
        .activeFragment
        .effects
        .slice(0)
        .reverse()
        .map(efid => this.$store.state.effects[efid]);
    },
    activeFilter: {
      get() {
        return this.$store.state.timelineFilter;
      },
      set(value) {
        this.$store.dispatch('setTimelineFilter', value);
      }
    },
    filteredAnimations() {
      return this.animations.filter(item => matchFilter(item, this.activeFilter, this.$store.state.shapeNames, this.videoSeriesTagMode));
    },
    height() {
      return this.$store.state.timelineHeight;
    },
    canSplit() {
      // 增加音频剪切
      var thatAudio = this.$store.state.audios[this.selectedAudioId];
      if(this.activeFragment&&thatAudio){
        return this.current - thatAudio.start >= 400 && thatAudio.start + thatAudio.duration - this.current >= 400;
      }
      //end
      if (!(this.activeFragment && this.selectedEffect)) {
        return false;
      }
      if(this.selectedEffect.shakinessId){
        return false
      }
      if (this.selectedEffect.container) {
        return false;
      }
      if ((this.selectedEffect.type === 'video' || this.selectedEffect.type === 'lottie') && this.selectedEffect.content.loop) {
        return false;
      }
      return this.current - this.selectedEffect.start >= 400 && this.selectedEffect.start + this.selectedEffect.duration - this.current >= 400;
    },
    playerVisible() {
      const { posterVisible, previewVisible, templatePlayerVisible, currentIcon, currentPresetTemplate, clippingEffect } = this.$store.state;
      return posterVisible || previewVisible || templatePlayerVisible || currentPresetTemplate || currentIcon === 'subtitle' || currentIcon === 'videoscript' || !!clippingEffect;
    },
    selectedAudioId() {
      return this.$store.state.selectedAudioId;
    },
    canDelete() {
      const { selectedEffectIds } = this.$store.state;
      return selectedEffectIds.length > 1 || this.selectedEffectId || this.selectedAudioId;
    },
    scrollAreaWidth() {
      return Math.max.apply(Math, [this.duration].concat(this.activeFragment.effects.map(id => {
        const ef = this.$store.state.effects[id];
        return ef ? ef.start + ef.duration : 0;
      }))) * 0.1 * this.scale;
    },
    videoSeriesTagMode() {
      return this.$store.state.videoSeriesTagMode;
    },
    menuCollapsed() {
      return this.$store.state.menuCollapsed;
    },
    snappable() {
      return { effects: this.filteredAnimations, audios: this.audios };
    },
    canFix() {
      if(this.$store.state.audios[this.selectedAudioId]){
        return false
      }
      return this.canSplit && (this.selectedEffect.type === 'video' || this.selectedEffect.type === 'lottie');
    }
  },
  components: {
    "track-group": TrackGroup,
    "timeline-scroll": TimelineScroll,
    "timeline-handle": TimelineHandle,
    'timeline-zoom': TimelineZoom,
    'selection-actions': SelectionActions,
    'duration-editor': DurationEditor,
    'timeline-scale': TimelineScale,
    'timeline-filter': TimelineFilter,
    record: Record
  },
  methods: {
    formatTooltip(value) {
      return formatTimeInterval(value);
    },
    processPlay() {
      let currentProgress = new Date() - this.start + this.prevProgress;
      if (currentProgress < this.duration) {
        this.current = currentProgress;
        this.playAF = requestAnimationFrame(this.processPlay); // TODO: play video if needed
      } else {
        this.playing = false;
        this.current = 0;
      }
    },
    togglePlay() {
      if (!this.playing) {
        this.playing = true;
        this.start = +new Date();
        this.prevProgress = this.current;
        this.playAF = requestAnimationFrame(this.processPlay);
      } else {
        this.pause();
      }
    },
    pause() {
      this.playing = false;
      cancelAnimationFrame(this.playAF);
      this.playAF = null;
    },
    changeCurrent(value) {
      if (this.playing) {
        this.pause();
        this.current = value;
        setTimeout(this.togglePlay, 0);
      } else {
        this.current = value;
      }
    },
    // backward() {
    //   this.changeCurrent(Math.max(0, this.current - 100));
    // },
    // forward() {
    //   this.changeCurrent(Math.min(this.duration, this.current + 100));
    // },
    reload() {
      this.stop();
      setTimeout(this.togglePlay, 0);
    },
    stop() {
      this.current = 0;
      this.pause();
    },
    splitAudio(){
      let obj;
      var _t = this;
      const audio = _t.$store.state.audios[this.selectedAudioId];
      const allaudios = this.$store.state.audios;
      const splitPoint = this.current - audio.start;
      const playbackRate = clamp(+audio.playbackRate || 1, .1, 10);
      const crop = audio.crop || [0, audio.duration / 1e3];
      const cropPoint = clamp(crop[0] + splitPoint / 1e3 * playbackRate, crop[0], crop[1]);
      const currentVideoCrop = [crop[0], crop[1]];
      const nextVideoCrop = [cropPoint, crop[1]];
      obj = { current: currentVideoCrop, next: nextVideoCrop };
      let audioId = '' + (+new Date());
      _t.$store.commit('handleAudios', {
          ...allaudios,
          [audio.id]: {
            ...audio,
            duration: splitPoint,
            crop: obj.current,
          },
          [audioId]: JSON.parse(JSON.stringify({
            ...audio,
            id: audioId,
            start: _t.$store.state.audios[this.selectedAudioId].start + splitPoint,
            duration: _t.$store.state.audios[this.selectedAudioId].duration - splitPoint,
            crop: obj.next 
          }))
        });
        this.activeFragment.audios = [].concat.apply([], this.activeFragment.audios.map(efid => {
          if (efid === audio.id) {
            return [efid, audioId];
          }
          return efid;
        }));
    },
    split() {
      const audio = this.$store.state.audios[this.selectedAudioId];
      if(audio){
        this.splitAudio()
        return
      }
      const effect = this.selectedEffect;
      if (effect) {
        let obj;
        const effects = this.$store.state.effects;
        const isVideo = effect.type === 'video';
        const isLottie = effect.type === 'lottie';
        const splitPoint = this.current - effect.start;

        if (isVideo) {
          const playbackRate = clamp(+effect.content.playbackRate || 1, .1, 10);
          const crop = effect.content.crop || [0, effect.duration / 1e3];
          const cropPoint = clamp(crop[0] + splitPoint / 1e3 * playbackRate, crop[0], crop[1]);
          const currentVideoCrop = [crop[0], crop[1]];
          const nextVideoCrop = [cropPoint, crop[1]];
          obj = { current: currentVideoCrop, next: nextVideoCrop };
        } else if (isLottie) {
          const crop = effect.content.crop || [0, effect.duration / 1e3];
          const cropPoint = clamp(crop[0] + splitPoint / 1e3, crop[0], crop[1]);
          const currentVideoCrop = [crop[0], cropPoint];
          const nextVideoCrop = [cropPoint, crop[1]];
          obj = { current: currentVideoCrop, next: nextVideoCrop };
        }
        let effectId = '' + (+new Date());

        let children;
        let childEffects = {};
        console.log(obj.current)
        if (effect.children) {
          children = effect.children.map((c, i) => {
            const childEffectId = [effectId, i].join('c');
            const childEffect = effects[c];
            childEffects[childEffectId] = JSON.parse(JSON.stringify({
              ...childEffect,
              id: childEffectId,
              container: effectId,
              children: undefined
            }));
            return childEffectId;
          });
        }
        this.$store.commit('handleEffects', {
          ...effects,
          ...childEffects,
          [this.selectedEffect.id]: {
            ...this.selectedEffect,
            duration: splitPoint,
            content: {
              ...this.selectedEffect.content,
              ...(isVideo || isLottie ? { crop: obj.current } : {})
            }
          },
          [effectId]: JSON.parse(JSON.stringify({
            ...this.selectedEffect,
            id: effectId,
            container: undefined,
            start: this.selectedEffect.start + splitPoint,
            duration: this.selectedEffect.duration - splitPoint,
            children,
            content: {
              ...this.selectedEffect.content,
              ...(isVideo || isLottie ? { crop: obj.next } : {})
            }
          }))
        });
        this.activeFragment.effects = [].concat.apply([], this.activeFragment.effects.map(efid => {
          if (efid === this.selectedEffect.id) {
            return [efid, effectId];
          }
          return efid;
        }));
      }
    },
    fixFrame() {
      const effect = this.selectedEffect;
      if (effect) {
        let obj;
        const effects = this.$store.state.effects;
        const isVideo = effect.type === 'video';
        const splitPoint = this.current - effect.start;

        if (isVideo) {
          const playbackRate = clamp(+effect.content.playbackRate || 1, .1, 10);
          const crop = effect.content.crop || [0, effect.duration / 1e3];
          const cropPoint = clamp(crop[0] + splitPoint / 1e3 * playbackRate, crop[0], crop[1]);
          const currentVideoCrop = [crop[0], crop[1]];
          const nextVideoCrop = [cropPoint + .03, crop[1]];
          obj = { current: currentVideoCrop, next: nextVideoCrop, cropPoint };
        } else {
          // TODO: lottie
          const crop = effect.content.crop || [0, effect.duration / 1e3];
          const cropPoint = clamp(crop[0] + splitPoint / 1e3, crop[0], crop[1]);
          const currentVideoCrop = [crop[0], cropPoint];
          const nextVideoCrop = [cropPoint, crop[1]];
          obj = { current: currentVideoCrop, next: nextVideoCrop, cropPoint };
        }
        const effectId = '' + (+new Date());
        const fixId = '' + (+effectId + 1);

        this.$store.commit('handleEffects', {
          ...effects,
          [this.selectedEffect.id]: {
            ...this.selectedEffect,
            duration: splitPoint,
            content: {
              ...this.selectedEffect.content,
              crop: obj.current
            }
          },
          [fixId]: JSON.parse(JSON.stringify(isVideo ? {
            ...this.selectedEffect,
            id: fixId,
            type: 'image',
            container: undefined,
            start: this.selectedEffect.start + splitPoint,
            duration: 3e3,
            content: {
              ...this.selectedEffect.content,
              crop: undefined,
              src: this.selectedEffect.content.src + `?x-oss-process=video/snapshot,t_${Math.round(obj.cropPoint * 1e3)}`,
              name: 'Fixed frame: ' + this.selectedEffect.content.name
            }
          } : {
            ...this.selectedEffect,
            id: fixId,
            container: undefined,
            start: this.selectedEffect.start + splitPoint,
            duration: 3e3,
            content: {
              ...this.selectedEffect.content,
              crop: [obj.cropPoint, obj.cropPoint],
              name: 'Fixed frame: ' + this.selectedEffect.content.name
            }
          })),
          [effectId]: JSON.parse(JSON.stringify({
            ...this.selectedEffect,
            id: effectId,
            container: undefined,
            start: this.selectedEffect.start + splitPoint + 3e3,
            duration: this.selectedEffect.duration - splitPoint - 30,
            content: {
              ...this.selectedEffect.content,
              crop: obj.next
            }
          }))
        });
        this.activeFragment.effects = [].concat.apply([], this.activeFragment.effects.map(efid => {
          if (efid === this.selectedEffect.id) {
            return [efid, fixId, effectId];
          }
          return efid;
        }));
      }
    },
    handleKeyDown(e) {
      // 按空格时切换播放状态
      if (!this.playerVisible && !/input|textarea|button/i.test(e.target.tagName)) {
        switch (e.keyCode) {
          case 32:
            e.preventDefault();
            this.togglePlay();
            break;
          case 67: {
            const ctrlKey = isMac && e.metaKey || e.ctrlKey;
            if (!ctrlKey && this.canSplit) {
              e.preventDefault();
              this.split();
            }
          }
            break;
        }
      }
    },
    autoRemoveSelection() {
      this.$store.dispatch('autoRemoveSelection');
    },
    clickCurrentAudio(e){
      console.log(e,'1111111111111111111111111111111')
    },
    handleAudioChange(e) {
      const audioTrack = e[0];
      this.audios = this.audios.map(a => a.id === audioTrack.id ? audioTrack : a);
    },
    handleScaleChange(e) {
      const prev = this.scale;
      this.scale = e;
      if (this.offset) {
        this.offset = Math.max(
          (this.offset + this.availableWidth / 2) * e / prev - this.availableWidth / 2,
          0
        );
      }
    },
    handleMouseWheel(e) {
      if (e.altKey) {
        e.preventDefault();
        const bcr = this.$el.getBoundingClientRect();
        const usedWidth = Math.min(this.duration * .1 * this.scale, this.availableWidth);
        const x = clamp(e.clientX - bcr.left - 250, 0, usedWidth) / usedWidth;

        const prev = this.scale;
        const prevZoom = Math.log10(prev);
        const nextZoom = clamp(e.deltaY > 0 ? prevZoom - .1 : prevZoom + .1, -1, 1);
        if (prevZoom !== nextZoom) {
          const nextScale = Math.pow(10, nextZoom);

          this.scale = nextScale;
          this.offset = Math.max(
            (this.offset + this.availableWidth * x) * nextScale / prev - this.availableWidth * x,
            0
          );
        }
      } else if (e.shiftKey) {
        const maxUsedWidth = Math.max(this.duration * .1 * this.scale, this.availableWidth);
        this.offset = clamp(e.deltaY > 0 ? this.offset + 100 : this.offset - 100, 0, maxUsedWidth - this.availableWidth);
      }
    },
    handleTrackSnap(t) {
      if (this.snapTimeout) {
        clearTimeout(this.snapTimeout);
      }
      this.snapping = true;
      this.snapTime = t;
      this.snapTimeout = setTimeout(() => {
        this.snapTimeout = 0;
        this.snapping = false;
      }, 500);
    }
  },
  mounted() {
    document.body.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    if (this.snapTimeout) {
      clearTimeout(this.snapTimeout);
    }
    document.body.removeEventListener('keydown', this.handleKeyDown);
  },
  watch: {
    'activeFragment.id'() {
      this.current = 0;
      this.$store.commit('handleEffectSelect', []);
      if (this.playing) {
        this.pause();
      }
    },
    selectedEffectId(val) {
      if (val && !this.playing && val !== 'BACKGROUND_SELECT') {
        let effect = this.selectedEffect;
        if (this.selectedEffect.container) {
          const container = this.$store.state.effects[this.selectedEffect.container];
          if (container) {
            effect = container;
          }
        }

        const { start, duration } = effect;
        if (this.current < start || this.current > +start + (+duration)) {
          this.current = +start + (
            this.selectedEffect.enter === 'none'
              ? 0
              : +this.selectedEffect.enterDuration
          );
        }
      }
    },
    playerVisible(val) {
      if (val && this.playing) {
        this.pause();
      }
    },
    audioShowing(val) {
      if (!val) {
        this.$store.commit('handleAudioSelect', null);
      }
    },
    menuCollapsed() {
      setTimeout(() => {
        if (this.$refs.scroll) {
          this.$refs.scroll.handleWidth();
        }
      }, 200);
    },
    recordStatus(val) {
      console.log(val)
      if (val == 2) {
        console.log('录音开始')
        this.togglePlay()
      } else if(val === 3) {
        this.pause()
        console.log('录音结束')
      }
    }
  }
};
</script>

<style>
.timeline-header {
  background: #f6f6f6;
  height: 40px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06), 0 0 3px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
}

.timeline-operation-area {
  position: absolute;
  left: 8px;
  top: 6px;
  bottom: 0;
  padding-bottom: 6px;
  display: flex;
  align-items: center;
}
.timeline-operation-record{
  position: absolute;
  top: 0;
  bottom: 0;
  left: 255px;
}

.timeline-operation-area__btn {
  padding: 0 10px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 16px;
  margin: 0 6px;
}

.timeline-operation-area__btn:hover,
.timeline-operation-area__btn:focus {
  color: rgba(0, 0, 0, 0.8);
}

.timeline-operation-area__btn.ant-btn-primary {
  color: #fff;
}

.timeline-operation-area__btn--selected {
  color: rgba(0, 127, 255, 0.75);
  background-color: rgba(0, 0, 0, 0.08);
}

.timeline-operation-area__btn--selected:hover,
.timeline-operation-area__btn--selected:focus {
  color: #007eff;
  background-color: rgba(0, 0, 0, 0.1);
}

.timeline-operation-area__btn:disabled {
  opacity: 0.5;
}

/* .timeline-operation-area .timeline-operation-area__btn {
  font-size: 16px;
} */

.slide-container {
  overflow: hidden;
  padding-left: 250px;
  position: relative;
  padding-bottom: 100vh;
  margin-bottom: -100vh;
}

.slide-container:before {
  display: block;
  content: "";
  height: 29px;
  width: 1px;
  margin-left: -1px;
}

.timeline-controls {
  display: flex;
  align-items: center;
  position: absolute;
  top: 8px;
  left: 50%;
  bottom: 0;
  box-sizing: border-box;
  padding: 0 10px 8px;
  transform: translate(-50%);
}

.timeline-controls__button.ant-btn {
  border: 1px solid rgba(0, 0, 0, 0.65);
  color: rgba(0, 0, 0, 0.65);
  background: transparent;
  line-height: 16px;
  margin: 0 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.timeline-controls__button.ant-btn .anticon {
  line-height: 12px;
}

.timeline-controls__button .anticon-reload {
  font-size: 12px;
}

.timeline-controls__button .anticon-caret-right,
.timeline-controls__button .anticon-pause {
  transition: 0s;
}

.timeline-controls__button .anticon-caret-right,
.timeline-controls__button .anticon-forward {
  margin-left: 2px;
}

.timeline-controls__button .anticon-backward {
  margin-left: -2px;
}

.timeline-controls__button:hover,
.timeline-controls__button:focus {
  color: rgba(0, 0, 0, 0.8);
}

.timeline-controls__button:disabled {
  color: rgba(0, 0, 0, 0.25);
  background-color: #f5f5f5;
  border-color: #d9d9d9;
}

.slide-container__body {
  background: #f3f3f3;
  margin-left: -1px;
  position: absolute;
  left: 250px;
  right: 0;
  top: 0;
  bottom: 0;
  box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.05);
}

.slide-container__box {
  height: 29px;
  box-shadow: 1px 0 0 rgba(0, 0, 0, 0.1);
  z-index: 1;
  position: relative;
}

.slide-container__box--shadowed {
  box-shadow: none;
}

.slide-container__box--shadowed:after {
  content: "";
  position: absolute;
  top: 0;
  left: 100%;
  height: 100vh;
  width: 999vw;
  background: rgba(255, 255, 255, 0.75);
  z-index: 0;
  pointer-events: none;
  box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.08);
}

.slide-container__slider {
  margin: 0;
  height: 29px;
  z-index: 1;
  position: relative;
}

.slide-container__slider .el-slider__runway {
  margin: 0;
  height: 100%;
  background: transparent;
}

.slide-container__slider .el-slider__bar,
.slide-container__slider .el-slider__stop {
  display: none;
}

.slide-container__slider .el-slider__button-wrapper {
  top: 8px;
  height: 20px;
}

.slide-container__slider .disabled .el-slider__button-wrapper {
  opacity: 0.6;
}

.slide-container__slider .el-slider__button-wrapper:after {
  content: "";
  width: 1px;
  height: 100vh;
  background: #b93544;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%);
  pointer-events: none;
}

.slide-container__slider .el-slider__button {
  border: none;
  border-radius: 0;
  background: url("/preview/icon/handle.png") center no-repeat;
  background-size: contain;
  width: 15px;
  height: 18px;
  transform: none;
  position: relative;
  z-index: 1;
}

.slide-container__snap {
  position: absolute;
  top: 0;
  width: 1px;
  height: 100%;
  pointer-events: none;
  background: #40a9ff;
  transition: opacity 0.3s;
  opacity: 0;
  z-index: 1;
}

.slide-container__snap--current {
  opacity: 1;
}

.timeline-tracks {
  height: 100px;
  overflow: auto;
  position: relative;
  z-index: 0;
  border-top: 1px solid #e8e8e8;
  background: #fff;
}

.timeline-tracks:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 100%;
  border-right: 1px solid #e8e8e8;
}

.icon-stop-button {
  width: 9px;
  height: 9px;
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.75);
}
</style>
